import React,{useEffect,useMemo,useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import BookingCalendar from '../components/BookingCalendar';
import BookingReview from '../components/BookingReview';
import BookingTripChoices from '../components/BookingTripChoices';
import WaitlistForm from '../components/WaitlistForm';
import {MARKETING_CONSENT_VERSION} from '../config/demo';
import {availableSeats,formatDate,formatTime,money} from '../utils/booking';
import supabase from '../supabase/supabase';
import './booking-calendar.css';

const {FiArrowLeft,FiCheck,FiFish,FiUsers}=FiIcons;
const REFUND_TABLE='stripe_inventory_collision_refunds_1789618507617';
const initialForm={
  party_size: 1,
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  customer_message: '',
  marketing_opt_in: false,
  discount_amount: 0,
  discount_eligible: false,
  marketing_consent_version: MARKETING_CONSENT_VERSION
};

const getPaymentParams=()=>{
  const searchParams=new URLSearchParams(window.location.search);
  if (searchParams.has('payment')) return searchParams;
  const hash=window.location.hash;
  const queryStart=hash.indexOf('?');
  if (queryStart===-1) return searchParams;
  return new URLSearchParams(hash.slice(queryStart+1));
};

const clearPaymentParams=()=>{
  const url=new URL(window.location.href);
  url.searchParams.delete('payment');
  url.searchParams.delete('session_id');
  const queryStart=url.hash.indexOf('?');
  if (queryStart!==-1) {
    const route=url.hash.slice(0,queryStart);
    const params=new URLSearchParams(url.hash.slice(queryStart+1));
    params.delete('payment');
    params.delete('session_id');
    url.hash=params.size ? `${route}?${params}` : route;
  }
  window.history.replaceState({},'',`${url.pathname}${url.search}${url.hash}`);
};

const calculateDiscount=(pricePerSeat,eligible,configuration)=>{
  if (!eligible || !configuration?.enabled) return 0;
  const percentage=Number(configuration.value || 0);
  if (configuration.applies_to!=='ONE_ELIGIBLE_ANGLER') return 0;
  return Math.min(pricePerSeat*percentage/100,pricePerSeat);
};

export default function BookingPage({data,actions,embedded=false}) {
  const [selectedDate,setSelectedDate]=useState('');
  const [sessionId,setSessionId]=useState('');
  const [form,setForm]=useState(initialForm);
  const [step,setStep]=useState(1);
  const [error,setError]=useState('');
  const [submitting,setSubmitting]=useState(false);
  const [paidReservation,setPaidReservation]=useState(null);
  const [collisionRefund,setCollisionRefund]=useState(null);
  const selectedSession=data.sessions.find((item)=>item.id===sessionId);
  const selectedType=data.tripTypes.find((item)=>item.id===selectedSession?.trip_type_id);
  const sessions=useMemo(()=>data.sessions
    .filter((session)=>session.date>=new Date().toISOString().slice(0,10))
    .map((session)=>({...session,tripType:data.tripTypes.find((type)=>type.id===session.trip_type_id)}))
    .filter((session)=>session.tripType),[data]);
  const selectedDateSessions=sessions.filter((session)=>session.date===selectedDate);

  useEffect(()=>{
    const params=getPaymentParams();
    if (embedded && params.get('payment')==='cancelled') {
      setStep(9);
      return undefined;
    }
    const checkoutSessionId=params.get('session_id');
    if (params.get('payment')!=='success' || !checkoutSessionId) return undefined;
    let cancelled=false;
    let attempts=0;
    const findOutcome=async()=>{
      const [reservationResult,refundResult]=await Promise.all([
        supabase.from('reservations_1789506688831')
          .select('*')
          .eq('stripe_checkout_session_id',checkoutSessionId)
          .eq('status','CONFIRMED')
          .maybeSingle(),
        supabase.from(REFUND_TABLE)
          .select('*')
          .eq('stripe_checkout_session_id',checkoutSessionId)
          .maybeSingle()
      ]);
      if (cancelled) return;
      if (reservationResult.error) {
        setError(reservationResult.error.message);
        return;
      }
      if (reservationResult.data) {
        setPaidReservation(reservationResult.data);
        setStep(5);
        return;
      }
      if (refundResult.error) {
        setError(refundResult.error.message);
        return;
      }
      if (refundResult.data) {
        setCollisionRefund(refundResult.data);
        setStep(8);
        return;
      }
      attempts+=1;
      if (attempts<20) {
        window.setTimeout(findOutcome,1500);
      } else {
        setError('Payment succeeded,but the reservation is still being confirmed. Please refresh shortly.');
      }
    };
    findOutcome();
    return ()=>{cancelled=true;};
  },[embedded]);

  const handleDateSelect=(date)=>{
    setSelectedDate(date);
    setSessionId('');
    setError('');
    setStep(2);
  };

  const handleTripSelect=(id)=>{
    setSessionId(id);
    setForm({...form,party_size: 1});
    setError('');
  };

  const updateForm=({target})=>{
    const next={
      ...form,
      [target.name]: target.type==='checkbox'
        ? target.checked
        : target.name==='party_size'
          ? Number(target.value)
          : target.value
    };
    if (target.name==='discount_eligible' && selectedType) {
      next.discount_amount=calculateDiscount(
        Number(selectedType.price_per_seat),
        target.checked,
        data.guide.military_discount
      );
    }
    setForm(next);
  };

  const startCheckout=async()=>{
    if (submitting) return;
    setSubmitting(true);
    setError('');
    const {data:result,error:checkoutError}=await supabase.functions.invoke(
      'create-checkout-session',
      {body:{
        trip_session_id: sessionId,
        party_size: form.party_size,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        customer_message: form.customer_message,
        marketing_opt_in: form.marketing_opt_in,
        marketing_consent_version: form.marketing_consent_version,
        discount_eligible: form.discount_eligible,
        deployment: embedded ? 'embed' : 'standalone'
      }}
    );
    if (checkoutError || !result?.url) {
      setSubmitting(false);
      setError(checkoutError?.message || 'Unable to start Stripe Checkout.');
      return;
    }
    window.location.assign(result.url);
  };

  const submitWaitlist=async(values)=>{
    const result=await actions.addWaitlistEntry({...values,trip_session_id: sessionId});
    if (result.ok) setStep(6);
    return result;
  };

  const restart=()=>{
    clearPaymentParams();
    setSelectedDate('');
    setSessionId('');
    setForm(initialForm);
    setPaidReservation(null);
    setCollisionRefund(null);
    setError('');
    setStep(1);
  };

  const joinCollisionWaitlist=()=>{
    const session=data.sessions.find((item)=>item.id===collisionRefund?.trip_session_id);
    if (!session) {
      restart();
      return;
    }
    setSelectedDate(session.date);
    setSessionId(session.id);
    setError('');
    setStep(7);
  };

  if (step===9) {
    return (
      <main className="booking-shell success-screen">
        <span className="eyebrow">Payment cancelled · No reservation created</span>
        <h1>Your booking is not confirmed.</h1>
        <p>Your payment was cancelled. No reservation was created and no seats were held.</p>
        <button className="button primary" onClick={restart}>Return to Fishing Dates</button>
      </main>
    );
  }

  if (step===8) {
    return (
      <main className="booking-shell success-screen collision-screen">
        <span className="success-icon"><SafeIcon icon={FiCheck} /></span>
        <span className="eyebrow">Payment refunded · No reservation created</span>
        <h1>That seat was booked by another guest.</h1>
        <p>
          That seat was booked by another guest while you were completing payment.
          <br /><br />
          Your payment has been refunded and no reservation was created.
          <br /><br />
          You can join the waitlist or choose another available date.
        </p>
        <div className="form-actions">
          <button className="button secondary" onClick={joinCollisionWaitlist}>JOIN WAITLIST</button>
          <button className="button primary" onClick={restart}>CHOOSE ANOTHER DATE</button>
        </div>
      </main>
    );
  }

  if (step===5) {
    return (
      <main className="booking-shell success-screen">
        <span className="success-icon"><SafeIcon icon={FiCheck} /></span>
        <span className="eyebrow">Deposit paid · Reservation confirmed</span>
        <h1>You’re on the boat.</h1>
        <p>
          Your Stripe Sandbox deposit was verified and your reservation is confirmed.
          {paidReservation && ` ${paidReservation.party_size} seats are reserved.`}
        </p>
        {paidReservation && (
          <div className="demo-email">
            <b>Payment recorded</b>
            <span>
              Deposit paid: {money(paidReservation.deposit_paid)} · Remaining balance:
              {' '}{money(paidReservation.balance_remaining)}
            </span>
          </div>
        )}
        <button className="button primary" onClick={restart}>Book Another Trip</button>
      </main>
    );
  }

  if (step===6) {
    return (
      <main className="booking-shell success-screen">
        <span className="success-icon"><SafeIcon icon={FiCheck} /></span>
        <span className="eyebrow">You’re on the list</span>
        <h1>We’ll let the guide know.</h1>
        <p>The guide will decide whom to contact if seats reopen. No seats were reserved.</p>
        <button className="button primary" onClick={restart}>Back to Fishing Dates</button>
      </main>
    );
  }

  return (
    <main className="booking-shell">
      <div className="booking-intro">
        <span className="eyebrow">Demo Fishing Charters</span>
        <h1>Book Your Fishing Trip</h1>
        <p>Choose a date,select your trip,and reserve your seats.</p>
      </div>
      <div className="stepper">
        {['Date','Trip','Details','Review'].map((label,index)=>(
          <span key={label} className={step>=index+1?'active':''}>
            <b>{index+1}</b>{label}
          </span>
        ))}
      </div>
      {step===1 && (
        <motion.div initial={{opacity: 0,y: 10}} animate={{opacity: 1,y: 0}}>
          <BookingCalendar
            sessions={sessions}
            reservations={data.reservations}
            adjustments={data.adjustments}
            onSelect={handleDateSelect}
          />
        </motion.div>
      )}
      {step===2 && (
        <BookingTripChoices
          date={selectedDate}
          sessions={selectedDateSessions}
          reservations={data.reservations}
          adjustments={data.adjustments}
          selectedSessionId={sessionId}
          onSelect={handleTripSelect}
          onBack={()=>setStep(1)}
          onContinue={()=>setStep(3)}
          onWaitlist={(id)=>{setSessionId(id);setStep(7);}}
        />
      )}
      {step===3 && selectedSession && selectedType && (
        <form
          className="customer-form"
          onSubmit={(event)=>{event.preventDefault();setError('');setStep(4);}}
        >
          <button type="button" className="back-link" onClick={()=>setStep(2)}>
            <SafeIcon icon={FiArrowLeft} /> Back to trip
          </button>
          <div className="selected-trip">
            <SafeIcon icon={FiUsers} />
            <span>
              <b>{selectedType.name}</b>
              <small>{formatDate(selectedSession.date,true)} · {formatTime(selectedSession.start_time)}</small>
            </span>
          </div>
          <label>
            Number in Your Party
            <select name="party_size" value={form.party_size} onChange={updateForm}>
              {Array.from({
                length: availableSeats(selectedSession,data.reservations,data.adjustments)
              },(_,index)=>index+1).map((size)=>(
                <option key={size} value={size}>
                  {size} {size===1?'guest':'guests'}
                </option>
              ))}
            </select>
          </label>
          <div className="form-grid">
            <label>Name<input name="customer_name" value={form.customer_name} onChange={updateForm} required /></label>
            <label>Email<input type="email" name="customer_email" value={form.customer_email} onChange={updateForm} required /></label>
          </div>
          <label>Phone<input type="tel" name="customer_phone" value={form.customer_phone} onChange={updateForm} required /></label>
          <label>Message for Guide<textarea name="customer_message" rows="3" value={form.customer_message} onChange={updateForm} /></label>
          {data.guide.military_discount?.enabled && (
            <label className="checkbox-label">
              <input type="checkbox" name="discount_eligible" checked={form.discount_eligible} onChange={updateForm} />
              I am eligible for the Military/Veteran discount.
              <small>
                {data.guide.military_discount.demo_rule.replace(/\banglers?\b/g,
                  (word)=>word.toLowerCase()==='anglers'?'guests':'guest')}
              </small>
            </label>
          )}
          <label className="checkbox-label">
            <input type="checkbox" name="marketing_opt_in" checked={form.marketing_opt_in} onChange={updateForm} />
            Send me fishing reports,open dates,and occasional trip updates.
          </label>
          <button className="button primary full">Review Reservation</button>
        </form>
      )}
      {step===4 && selectedSession && selectedType && (
        <BookingReview
          type={selectedType}
          session={selectedSession}
          form={form}
          discount={{amount: form.discount_amount}}
          onBack={()=>setStep(3)}
          onConfirm={startCheckout}
          error={error}
          submitting={submitting}
        />
      )}
      {step===7 && selectedSession && selectedType && (
        <WaitlistForm
          session={selectedSession}
          type={selectedType}
          onBack={()=>setStep(2)}
          onSubmit={submitWaitlist}
        />
      )}
      {step===2 && selectedDate && (
        <p className="booking-inventory-note">
          <SafeIcon icon={FiFish} /> Showing live seats for {formatDate(selectedDate)}.
        </p>
      )}
    </main>
  );
}